import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Droplet, Zap, Shield } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e27] relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Grid background */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'linear-gradient(0deg, rgba(0, 217, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 217, 255, 0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>

      <Card className="w-full max-w-md shadow-2xl bg-[#12172e] border-cyan-500/30 text-white relative z-10" data-testid="login-card">
        {/* Top glow line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>
        
        <CardHeader className="space-y-4 text-center pt-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              {/* SWATI Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_waterwise-24/artifacts/dpirsu80_logo_basic.png" 
                alt="SWATI Logo" 
                className="w-24 h-24 object-contain"
              />
            </div>
          </div>
          
          <div>
            <CardTitle className="text-3xl font-bold tracking-wider">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                SWATI - WMWR
              </span>
            </CardTitle>
            <CardDescription className="text-gray-400 mt-2 tracking-wide">WATER MINISTERS WAR ROOM</CardDescription>
            <p className="text-sm text-gray-500 mt-1">Water Ministers War Room</p>
          </div>

          {/* Status badges */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <div className="px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center gap-2">
              <Shield className="text-cyan-400" size={14} />
              <span className="text-cyan-400 font-semibold text-xs">SECURE</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-green-500/10 border border-green-500/30 flex items-center gap-2">
              <Zap className="text-green-400" size={14} />
              <span className="text-green-400 font-semibold text-xs">ONLINE</span>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/50 text-red-400" data-testid="login-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300 text-sm font-medium tracking-wide">USERNAME / EMAIL</Label>
              <Input
                id="email"
                type="text"
                placeholder="admin"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-slate-900/50 border-cyan-500/30 hover:border-cyan-500/50 focus:border-cyan-500 text-white placeholder:text-gray-500 transition-colors"
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300 text-sm font-medium tracking-wide">PASSWORD</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-slate-900/50 border-cyan-500/30 hover:border-cyan-500/50 focus:border-cyan-500 text-white placeholder:text-gray-500 transition-colors"
                data-testid="password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 border-0 text-white font-semibold tracking-wide py-6 text-base shadow-lg hover:shadow-cyan-500/30 transition-all"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  AUTHENTICATING...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Zap size={18} />
                  SIGN IN TO COMMAND CENTER
                </span>
              )}
            </Button>
          </form>
          
          <div className="mt-6 p-4 bg-slate-900/30 rounded-lg border border-slate-700/50">
            <p className="font-semibold mb-3 text-cyan-400 text-sm tracking-wide">LOGIN CREDENTIALS:</p>
            <div className="space-y-2 text-xs">
              <div className="p-2 bg-slate-800/50 rounded border border-slate-700/30">
                <p className="text-gray-400 mb-1">Admin</p>
                <p className="text-white font-mono">admin / Ladliji0502</p>
              </div>
            </div>
          </div>
        </CardContent>

        {/* Bottom glow line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>
      </Card>
    </div>
  );
};

export default Login;
